import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { payrollEngineApi } from '../../../services/api';
import { Spinner, EmptyState } from '../../../components/shared/index';

const SECTION_LABELS = {
  earnings:            { label: 'Earnings',            color: 'text-green-700',  bg: 'bg-green-50/60',  border: 'border-green-100' },
  pre_tax_deduction:   { label: 'Pre-tax deductions',  color: 'text-blue-700',   bg: 'bg-blue-50/60',   border: 'border-blue-100' },
  tax:                 { label: 'Taxes',               color: 'text-red-700',    bg: 'bg-red-50/60',    border: 'border-red-100' },
  post_tax_deduction:  { label: 'Post-tax deductions', color: 'text-amber-700',  bg: 'bg-amber-50/60',  border: 'border-amber-100' },
  employer_tax:        { label: 'Employer taxes',      color: 'text-purple-700', bg: 'bg-purple-50/60', border: 'border-purple-100' },
};

const SECTION_ORDER = ['earnings', 'pre_tax_deduction', 'tax', 'post_tax_deduction', 'employer_tax'];

function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);
}

export default function StubDetail({ stubId }) {
  const { data: stub, isLoading } = useQuery({
    queryKey: ['pay-stub', stubId],
    queryFn:  () => payrollEngineApi.getStub(stubId).then(r => r.data),
    enabled:  !!stubId,
  });

  if (isLoading) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;
  if (!stub) {
    return <EmptyState title="Stub not found" description="This pay stub may have been voided." />;
  }

  const lines = stub.lines || [];
  const grouped = SECTION_ORDER.reduce((acc, section) => {
    acc[section] = lines.filter(l => l.section === section);
    return acc;
  }, {});

  const sectionTotal = (section) =>
    grouped[section].reduce((s, l) => s + (Number(l.amount) || 0), 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Gross</p>
          <p className="text-lg font-bold text-gray-900 mt-1">{fmtMoney(stub.gross)}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Taxes</p>
          <p className="text-lg font-bold text-red-700 mt-1">
            {fmtMoney((Number(stub.federal_tax) || 0) + (Number(stub.state_tax) || 0) + (Number(stub.fica) || 0))}
          </p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Deductions</p>
          <p className="text-lg font-bold text-blue-700 mt-1">
            {fmtMoney((Number(stub.pre_tax_total) || 0) + (Number(stub.post_tax_total) || 0))}
          </p>
        </div>
        <div className="card p-3 bg-green-50/60">
          <p className="text-xs text-green-700 uppercase tracking-wide font-semibold">Net</p>
          <p className="text-lg font-bold text-green-800 mt-1">{fmtMoney(stub.net)}</p>
        </div>
      </div>

      {/* Meta */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        {stub.employee?.name && <span><strong className="text-gray-700">Employee:</strong> {stub.employee.name}</span>}
        {stub.period?.name && <span><strong className="text-gray-700">Period:</strong> {stub.period.name}</span>}
        {stub.period?.pay_date && (
          <span><strong className="text-gray-700">Pay date:</strong> {new Date(stub.period.pay_date).toLocaleDateString()}</span>
        )}
        {stub.status && <span className="capitalize"><strong className="text-gray-700">Status:</strong> {stub.status}</span>}
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {SECTION_ORDER.map((section) => {
          const sLines = grouped[section];
          if (!sLines.length) return null;
          const cfg = SECTION_LABELS[section];
          return (
            <div key={section} className={clsx('card p-0 overflow-hidden border', cfg.border)}>
              <div className={clsx('px-4 py-2 flex items-center justify-between', cfg.bg)}>
                <span className={clsx('text-xs font-semibold uppercase tracking-wide', cfg.color)}>
                  {cfg.label}
                </span>
                <span className={clsx('text-sm font-bold', cfg.color)}>
                  {fmtMoney(sectionTotal(section))}
                </span>
              </div>
              <ul className="divide-y divide-gray-100">
                {sLines.map((l, idx) => (
                  <li key={l.id ?? idx} className="flex items-center justify-between px-4 py-2 text-sm">
                    <div className="min-w-0">
                      <span className="text-gray-800">{l.label || l.name || l.description}</span>
                      {l.code && <span className="ml-2 text-xs text-gray-400">{l.code}</span>}
                    </div>
                    <span className="font-medium text-gray-900 shrink-0">{fmtMoney(l.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
